import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import api from '../../utils/api';
import { StatCard, Card, PageHeader, Badge } from '../../components/ui/UI';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { ClipboardList, Users, ShieldCheck, AlertTriangle, ArrowRight, Maximize2, X } from 'lucide-react';
import { format } from 'date-fns';
import './DashboardPage.css';

const BU_COLORS = { SNACKS: '#1a472a', DAIRIES: '#2d6a40', SWAN: '#4a9460', Management: '#7bbf8c', OTHER: '#a8d5b5' };
const PIE_COLORS = ['#1a472a', '#c0392b', '#c87533', '#4a9460', '#7bbf8c'];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload?.length) {
    return (
      <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: '6px', padding: '7px 10px', fontSize: '0.78rem', boxShadow: 'var(--shadow)' }}>
        <div style={{ fontWeight: 600, marginBottom: 2 }}>{label}</div>
        {payload.map((p, i) => (
          <div key={i} style={{ color: p.color || 'var(--text-2)' }}>{p.name}: {p.value}</div>
        ))}
      </div>
    );
  }
  return null;
};

// Chart wrapper component
const ChartCardWrapper = ({ chartId, title, children, onExpand }) => (
  <Card className="chart-card">
    <div className="chart-header">
      <span className="chart-title">{title}</span>
      <button 
        className="chart-expand-btn"
        onClick={() => onExpand(chartId)}
        title="Expand chart"
      >
        <Maximize2 size={14} />
      </button>
    </div>
    {children}
  </Card>
);

// Expanded chart modal
const ExpandedChartModal = ({ title, children, onClose }) => (
  createPortal(
    <div className="expanded-chart-modal-backdrop" onClick={onClose}>
      <div className="expanded-chart-modal" onClick={e => e.stopPropagation()}>
        <div className="expanded-chart-modal-header">
          <h2>{title}</h2>
          <button 
            className="expanded-chart-close-btn"
            onClick={onClose}
            title="Close expanded view"
          >
            <X size={20} />
          </button>
        </div>
        <div className="expanded-chart-content">
          {children}
        </div>
      </div>
    </div>,
    document.body
  )
);

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedChart, setExpandedChart] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/observations/stats'),
      api.get('/observations?limit=8')
    ]).then(([s, o]) => {
      setStats(s.data);
      setRecent(o.data.observations);
    }).finally(() => setLoading(false));
  }, []);

  // Helper to get top N items from stats arrays
  const getTop = (data, count) => (Array.isArray(data) ? data.slice(0, count) : []);

  if (loading) return (
    <div className="fade-in">
      <div className="page-header"><div><div className="skeleton" style={{ width: 160, height: 20 }} /></div></div>
      <div className="stats-grid">
        {[...Array(4)].map((_, i) => <div key={i} className="skeleton" style={{ height: 88 }} />)}
      </div>
    </div>
  );

  const safePercent = stats?.total ? ((stats.safe / stats.total) * 100).toFixed(1) : '0.0';

  return (
    <div className="fade-in">
      <PageHeader
        title="Dashboard"
        subtitle="BBS & VFL Observation Register Overview"
      />

      {/* Stats */}
      <div className="stats-grid">
        <StatCard label="Total Observations" value={stats?.total ?? 0} icon={<ClipboardList size={16} />} />
        <StatCard label="Safe Behaviors" value={stats?.safe ?? 0} icon={<ShieldCheck size={16} />} variant="safe" sub={`${safePercent}% of total`} />
        <StatCard label="Safe Ratio" value={`${safePercent}%`} icon={<ShieldCheck size={16} />} variant="safe" />
        <StatCard label="At-Risk Behaviors" value={stats?.atRisk ?? 0} icon={<AlertTriangle size={16} />} variant="risk" sub={`${100 - safePercent}% of total`} />
        <StatCard label="Business Units" value={stats?.byBU?.length ?? 0} icon={<Users size={16} />} variant="accent" sub="Active units" />
      </div>

      {/* Charts row */}
      <div className="charts-row">
        {/* Trend */}
        <ChartCardWrapper chartId="trend" title="Observations — Last 30 Days" onExpand={setExpandedChart}>
          <ResponsiveContainer width="100%" height={170}>
            <LineChart data={stats?.recent30 ?? []} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
              <XAxis dataKey="_id" tick={{ fontSize: 10, fill: 'var(--text-3)' }} tickFormatter={v => v ? v.slice(5) : ''} />
              <YAxis tick={{ fontSize: 10, fill: 'var(--text-3)' }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Line type="monotone" dataKey="count" stroke="var(--accent)" strokeWidth={1.8} dot={false} name="Observations" />
            </LineChart>
          </ResponsiveContainer>
        </ChartCardWrapper>

        {/* Behavior type pie */}
        <ChartCardWrapper chartId="behaviorType" title="Behavior Type" onExpand={setExpandedChart}>
          <div className="pie-wrap">
            <ResponsiveContainer width="100%" height={150}>
              <PieChart>
                <Pie data={[{ name: 'Safe', value: stats?.safe ?? 0 }, { name: 'At-Risk', value: stats?.atRisk ?? 0 }]} cx="50%" cy="50%" innerRadius={42} outerRadius={62} dataKey="value" paddingAngle={3}>
                  <Cell fill="var(--safe)" />
                  <Cell fill="var(--danger)" />
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="pie-legend">
              <div className="pie-legend-item"><span style={{ background: 'var(--safe)' }} /><span>Safe ({stats?.safe ?? 0})</span></div>
              <div className="pie-legend-item"><span style={{ background: 'var(--danger)' }} /><span>At-Risk ({stats?.atRisk ?? 0})</span></div>
            </div>
          </div>
        </ChartCardWrapper>

        {/* By BU */}
        <ChartCardWrapper chartId="byBU" title="By Business Unit" onExpand={setExpandedChart}>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={stats?.byBU ?? []} layout="vertical" margin={{ top: 0, right: 8, left: 4, bottom: 0 }}>
              <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--text-3)' }} allowDecimals={false} />
              <YAxis dataKey="_id" type="category" tick={{ fontSize: 10, fill: 'var(--text-2)' }} width={60} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" radius={[0, 3, 3, 0]} name="Count" label={{ position: 'right', fontSize: 10, fill: 'var(--text-2)' }}>
                {(stats?.byBU ?? []).map((entry, i) => (
                  <Cell key={i} fill={BU_COLORS[entry._id] || PIE_COLORS[i % PIE_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCardWrapper>
      </div>

      {/* Shift breakdown */}
      {stats?.byShift?.length > 0 && (
        <div className="charts-row" style={{ marginTop: 0 }}>
          <ChartCardWrapper chartId="topCategories" title="Top Behavior Categories" onExpand={setExpandedChart}>
            <ResponsiveContainer width="100%" height={140}>
              <BarChart data={getTop(stats?.byCategory, 5)} layout="vertical" margin={{ top: 0, right: 8, left: 4, bottom: 0 }}>
                <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--text-3)' }} allowDecimals={false} />
                <YAxis dataKey="_id" type="category" tick={{ fontSize: 10, fill: 'var(--text-2)' }} width={110} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" fill="var(--accent-light)" radius={[0, 3, 3, 0]} name="Count" label={{ position: 'right', fontSize: 10, fill: 'var(--text-2)' }} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCardWrapper>

          <ChartCardWrapper chartId="byShift" title="By Shift" onExpand={setExpandedChart}>
            <ResponsiveContainer width="100%" height={140}>
              <BarChart data={stats?.byShift ?? []} margin={{ top: 4, right: 8, left: -24, bottom: 0 }}>
                <XAxis dataKey="_id" tick={{ fontSize: 10, fill: 'var(--text-3)' }} />
                <YAxis tick={{ fontSize: 10, fill: 'var(--text-3)' }} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" fill="var(--accent)" radius={[3, 3, 0, 0]} name="Count" label={{ position: 'top', fontSize: 10, fill: 'var(--text-2)' }} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCardWrapper>

          {stats?.byObserver?.length > 0 && (
            <ChartCardWrapper chartId="topObservers" title="Top Observers" onExpand={setExpandedChart}>
              <ResponsiveContainer width="100%" height={140}>
                <BarChart data={getTop(stats?.byObserver, 5)} layout="vertical" margin={{ top: 0, right: 8, left: 4, bottom: 0 }}>
                  <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--text-3)' }} allowDecimals={false} />
                  <YAxis dataKey="_id" type="category" tick={{ fontSize: 9, fill: 'var(--text-2)' }} width={100} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" fill="var(--accent-light)" radius={[0, 3, 3, 0]} name="Count" label={{ position: 'right', fontSize: 10, fill: 'var(--text-2)' }} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCardWrapper>
          )}
        </div>
      )}

      {/* Top Antecedents & By Department & By Location */}
      {(stats?.byAntecedent?.length > 0 || stats?.byDepartment?.length > 0 || stats?.byLocation?.length > 0) && (
        <div className="charts-row" style={{ marginTop: 16 }}>
          {stats?.byAntecedent?.length > 0 && (
            <ChartCardWrapper chartId="topAntecedents" title="Top Antecedents Categories" onExpand={setExpandedChart}>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={getTop(stats?.byAntecedent, 5)} layout="vertical" margin={{ top: 0, right: 8, left: 10, bottom: 0 }}>
                  <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--text-3)' }} allowDecimals={false} />
                  <YAxis dataKey="_id" type="category" tick={{ fontSize: 9, fill: 'var(--text-2)' }} width={300} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" fill="var(--accent-light)" radius={[0, 3, 3, 0]} name="Count" label={{ position: 'right', fontSize: 10, fill: 'var(--text-2)' }} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCardWrapper>
          )}
          {stats?.byDepartment?.length > 0 && (
            <ChartCardWrapper chartId="byDepartment" title="By Department" onExpand={setExpandedChart}>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={getTop(stats?.byDepartment, 5)} layout="vertical" margin={{ top: 0, right: 8, left: 10, bottom: 0 }}>
                  <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--text-3)' }} allowDecimals={false} />
                  <YAxis dataKey="_id" type="category" tick={{ fontSize: 9, fill: 'var(--text-2)' }} width={150} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" fill="var(--accent-light)" radius={[0, 3, 3, 0]} name="Count" label={{ position: 'right', fontSize: 10, fill: 'var(--text-2)' }} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCardWrapper>
          )}
          {stats?.byLocation?.length > 0 && (
            <ChartCardWrapper chartId="byLocation" title="By Location" onExpand={setExpandedChart}>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={getTop(stats?.byLocation, 5)} layout="vertical" margin={{ top: 0, right: 8, left: 10, bottom: 0 }}>
                  <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--text-3)' }} allowDecimals={false} />
                  <YAxis dataKey="_id" type="category" tick={{ fontSize: 8, fill: 'var(--text-2)' }} width={150} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" fill="var(--accent-light)" radius={[0, 3, 3, 0]} name="Count" label={{ position: 'right', fontSize: 10, fill: 'var(--text-2)' }} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCardWrapper>
          )}
        </div>
      )}

      {/* Expanded Chart Modal */}
      {expandedChart === 'trend' && (
        <ExpandedChartModal title="Observations — Last 30 Days" onClose={() => setExpandedChart(null)}>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={stats?.recent30 ?? []} margin={{ top: 10, right: 30, left: 0, bottom: 10 }}>
              <XAxis dataKey="_id" tick={{ fontSize: 11, fill: 'var(--text-3)' }} />
              <YAxis tick={{ fontSize: 11, fill: 'var(--text-3)' }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Line type="monotone" dataKey="count" stroke="var(--accent)" strokeWidth={2} dot={{ fill: 'var(--accent)', r: 4 }} name="Observations" />
            </LineChart>
          </ResponsiveContainer>
        </ExpandedChartModal>
      )}

      {expandedChart === 'behaviorType' && (
        <ExpandedChartModal title="Behavior Type" onClose={() => setExpandedChart(null)}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
            <ResponsiveContainer width="100%" height={400}>
              <PieChart>
                <Pie data={[{ name: 'Safe', value: stats?.safe ?? 0 }, { name: 'At-Risk', value: stats?.atRisk ?? 0 }]} cx="50%" cy="50%" innerRadius={60} outerRadius={120} dataKey="value" paddingAngle={3} label>
                  <Cell fill="var(--safe)" />
                  <Cell fill="var(--danger)" />
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="pie-legend" style={{ gap: 20 }}>
              <div className="pie-legend-item"><span style={{ background: 'var(--safe)', width: 12, height: 12 }} /><span>Safe ({stats?.safe ?? 0})</span></div>
              <div className="pie-legend-item"><span style={{ background: 'var(--danger)', width: 12, height: 12 }} /><span>At-Risk ({stats?.atRisk ?? 0})</span></div>
            </div>
          </div>
        </ExpandedChartModal>
      )}

      {expandedChart === 'byBU' && (
        <ExpandedChartModal title="By Business Unit" onClose={() => setExpandedChart(null)}>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={stats?.byBU ?? []} layout="vertical" margin={{ top: 10, right: 30, left: 120, bottom: 10 }}>
              <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--text-3)' }} allowDecimals={false} />
              <YAxis dataKey="_id" type="category" tick={{ fontSize: 11, fill: 'var(--text-2)' }} width={110} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" radius={[0, 3, 3, 0]} name="Count" label={{ position: 'right', fontSize: 10, fill: 'var(--text-2)' }}>
                {(stats?.byBU ?? []).map((entry, i) => (
                  <Cell key={i} fill={BU_COLORS[entry._id] || PIE_COLORS[i % PIE_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ExpandedChartModal>
      )}

      {expandedChart === 'topCategories' && (
        <ExpandedChartModal title="Top Behavior Categories" onClose={() => setExpandedChart(null)}>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={stats?.byCategory ?? []} layout="vertical" margin={{ top: 10, right: 30, left: 150, bottom: 10 }}>
              <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--text-3)' }} allowDecimals={false} />
              <YAxis dataKey="_id" type="category" tick={{ fontSize: 11, fill: 'var(--text-2)' }} width={140} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" fill="var(--accent-light)" radius={[0, 3, 3, 0]} name="Count" label={{ position: 'right', fontSize: 10, fill: 'var(--text-2)' }} />
            </BarChart>
          </ResponsiveContainer>
        </ExpandedChartModal>
      )}

      {expandedChart === 'byShift' && (
        <ExpandedChartModal title="By Shift" onClose={() => setExpandedChart(null)}>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={stats?.byShift ?? []} margin={{ top: 10, right: 30, left: 0, bottom: 10 }}>
              <XAxis dataKey="_id" tick={{ fontSize: 11, fill: 'var(--text-3)' }} />
              <YAxis tick={{ fontSize: 11, fill: 'var(--text-3)' }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" fill="var(--accent)" radius={[3, 3, 0, 0]} name="Count" label={{ position: 'top', fontSize: 10, fill: 'var(--text-2)' }} />
            </BarChart>
          </ResponsiveContainer>
        </ExpandedChartModal>
      )}

      {expandedChart === 'topObservers' && (
        <ExpandedChartModal title="Top Observers" onClose={() => setExpandedChart(null)}>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={stats?.byObserver ?? []} layout="vertical" margin={{ top: 10, right: 30, left: 120, bottom: 10 }}>
              <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--text-3)' }} allowDecimals={false} />
              <YAxis dataKey="_id" type="category" tick={{ fontSize: 10, fill: 'var(--text-2)' }} width={110} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" fill="var(--accent-light)" radius={[0, 3, 3, 0]} name="Count" label={{ position: 'right', fontSize: 10, fill: 'var(--text-2)' }} />
            </BarChart>
          </ResponsiveContainer>
        </ExpandedChartModal>
      )}

      {expandedChart === 'topAntecedents' && (
        <ExpandedChartModal title="Top Antecedents Categories" onClose={() => setExpandedChart(null)}>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={stats?.byAntecedent ?? []} layout="vertical" margin={{ top: 10, right: 30, left: 350, bottom: 10 }}>
              <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--text-3)' }} allowDecimals={false} />
              <YAxis dataKey="_id" type="category" tick={{ fontSize: 10, fill: 'var(--text-2)' }} width={340} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" fill="var(--accent-light)" radius={[0, 3, 3, 0]} name="Count" label={{ position: 'right', fontSize: 10, fill: 'var(--text-2)' }} />
            </BarChart>
          </ResponsiveContainer>
        </ExpandedChartModal>
      )}

      {expandedChart === 'byDepartment' && (
        <ExpandedChartModal title="By Department" onClose={() => setExpandedChart(null)}>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={stats?.byDepartment ?? []} layout="vertical" margin={{ top: 10, right: 30, left: 180, bottom: 10 }}>
              <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--text-3)' }} allowDecimals={false} />
              <YAxis dataKey="_id" type="category" tick={{ fontSize: 11, fill: 'var(--text-2)' }} width={170} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" fill="var(--accent-light)" radius={[0, 3, 3, 0]} name="Count" label={{ position: 'right', fontSize: 10, fill: 'var(--text-2)' }} />
            </BarChart>
          </ResponsiveContainer>
        </ExpandedChartModal>
      )}

      {expandedChart === 'byLocation' && (
        <ExpandedChartModal title="By Location" onClose={() => setExpandedChart(null)}>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={stats?.byLocation ?? []} layout="vertical" margin={{ top: 10, right: 30, left: 180, bottom: 10 }}>
              <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--text-3)' }} allowDecimals={false} />
              <YAxis dataKey="_id" type="category" tick={{ fontSize: 11, fill: 'var(--text-2)' }} width={170} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" fill="var(--accent-light)" radius={[0, 3, 3, 0]} name="Count" label={{ position: 'right', fontSize: 10, fill: 'var(--text-2)' }} />
            </BarChart>
          </ResponsiveContainer>
        </ExpandedChartModal>
      )}
    </div>
  );
}
